import React from "react";
import { CELL, bestMonth, cellStyle, metricValue, fmtMetric, fmtMonth } from "./intel";

export function Legend({ t, note, partial }) {
  const items = [[CELL.best, t.best, "best"], [CELL.base, t.other, "other"], ...(partial ? [[CELL.partial, t.partialKey, "partial"]] : [])];
  return (
    <div className="mt-4 space-y-2" data-testid="matrix-legend">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-propertic-ink-2">
        {items.map(([st, label, id]) => (
          <span key={id} className="inline-flex items-center gap-1.5" data-testid={`matrix-legend-${id}`}>
            <span className="w-5 h-3.5 rounded" style={st} />{label}
          </span>
        ))}
      </div>
      {note && <p className="text-[11px] text-propertic-muted" data-testid="matrix-change-note">{note}</p>}
      <p className="text-[11px] text-propertic-muted">{t.legendNote}</p>
    </div>
  );
}

function MonthHead({ m, sel, onSelect, partial, isAR, t }) {
  const active = m === sel.month;
  return (
    <th scope="col" className="px-0.5 py-1.5 border-b hairline whitespace-nowrap align-bottom">
      {m === partial && <span className="block text-center text-[9px] font-mono-brand tracking-[0.1em] uppercase text-propertic-gold mb-0.5">{t.inProgress}</span>}
      <button type="button" onClick={() => onSelect({ month: m })} aria-pressed={active}
        className={`w-full rounded-full px-2 py-1 text-[11px] font-semibold transition-colors ${active ? "bg-propertic-navy text-propertic-paper" : "text-propertic-ink-2 hover:bg-propertic-sand"}`}
        data-testid={`matrix-month-${m}`}>{fmtMonth(m, isAR, true)}</button>
    </th>
  );
}

export default function ActivityMatrix({ rows, months, allMonths, metric, sel, onSelect, partial, isAR, t }) {
  return (
    <div className="overflow-x-auto rounded-2xl border hairline bg-white" data-testid="activity-matrix">
      <table className="w-full border-separate border-spacing-0 text-[12px]" aria-label={t.matrix}>
        <thead>
          <tr>
            <th scope="col" className="sticky start-0 z-10 bg-white text-start px-3 py-2 align-bottom font-mono-brand text-[10px] tracking-[0.12em] uppercase text-propertic-muted border-b hairline min-w-[170px]">{t.area}</th>
            {months.map((m) => <MonthHead key={m} m={m} sel={sel} onSelect={onSelect} partial={partial} isAR={isAR} t={t} />)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const active = sel.key === r.key;
            const best = bestMonth(metric, r, months, allMonths, partial);
            return (
              <tr key={r.key} data-testid={`matrix-row-${r.slug}`}>
                <th scope="row" className={`sticky start-0 z-10 text-start px-3 py-1 font-medium border-b hairline transition-colors ${active ? "bg-propertic-sand" : "bg-white"}`}>
                  <button type="button" onClick={() => onSelect({ key: r.key })} className="flex items-center gap-2 text-start hover:text-propertic-gold focus:outline-none focus-visible:underline" data-testid={`matrix-area-${r.slug}`}>
                    <span className="w-5 text-[10px] text-propertic-muted tabular-nums">{idx + 1}</span>
                    <span>{r.label}</span>
                  </button>
                </th>
                {months.map((m) => {
                  const v = metricValue(metric, r, allMonths, m);
                  const label = fmtMetric(metric, v, isAR);
                  const isSel = active && m === sel.month;
                  return (
                    <td key={m} className="p-[3px] border-b hairline">
                      <button type="button" onClick={() => onSelect({ key: r.key, month: m })}
                        aria-label={`${r.label}, ${fmtMonth(m, isAR)}${m === partial ? ` (${t.inProgress})` : ""}: ${t.metrics[metric]} ${label}${m === best ? ` · ${t.best}` : ""}`}
                        className={`w-full min-w-[86px] whitespace-nowrap rounded-lg px-1.5 py-1.5 text-center tabular-nums transition-transform hover:scale-[1.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-propertic-gold ${m === best ? "font-semibold" : "font-medium"} ${isSel ? "ring-2 ring-propertic-gold ring-offset-1" : ""}`}
                        style={cellStyle(v, m === best, m === partial)} data-testid={`matrix-cell-${r.slug}-${m}`} data-best={m === best ? "true" : undefined}>
                        {label}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!rows.length && <p className="px-4 py-6 text-sm text-propertic-muted" data-testid="matrix-no-match">{t.noMatch}</p>}
    </div>
  );
}
